import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { api } from "@/lib/api";

/** Global admin banner for standing orders within 7 days of (or past) their annual
 *  review. Each item clears when the admin dismisses it OR when the Medical Director
 *  re-signs (the review date moves out of the window). */
export function StandingOrderReviewBanner() {
    const [alerts, setAlerts] = useState([]);

    const load = useCallback(() => {
        api.get("/admin/standing-orders/review-alert")
            .then((r) => setAlerts(r.data.alerts || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 120000);
        return () => clearInterval(id);
    }, [load]);

    const dismiss = async (id) => {
        setAlerts((prev) => prev.filter((a) => a.id !== id));
        try { await api.post(`/admin/standing-orders/${id}/dismiss-review-alert`); } catch { load(); }
    };

    if (alerts.length === 0) return null;

    return (
        <div data-testid="admin-so-review-banner" className="mx-4 mt-3 space-y-2">
            {alerts.map((a) => (
                <div key={a.id} data-testid={`admin-so-review-banner-${a.id}`}
                    className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm ${a.overdue ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
                    <AlertTriangle className={`w-4 h-4 shrink-0 ${a.overdue ? "text-red-600" : "text-amber-600"}`} />
                    <span className="min-w-0 flex-1">
                        <b>{a.title}</b> ({a.facility_name || "facility"}) —{" "}
                        {a.overdue
                            ? <span className="font-semibold">OVERDUE for annual review</span>
                            : <>due for annual review in <b>{a.days_until} day(s)</b></>}
                        . {a.medical_director_name ? `Awaiting re-signature by ${a.medical_director_name}.` : "No Medical Director assigned."}
                    </span>
                    <Link to="/admin/standing-orders" data-testid={`admin-so-review-view-${a.id}`}
                        className="shrink-0 text-xs font-semibold underline hover:no-underline">Review</Link>
                    <button data-testid={`admin-so-review-dismiss-${a.id}`} onClick={() => dismiss(a.id)}
                        className="shrink-0 p-1 rounded hover:bg-black/5" title="Dismiss">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ))}
        </div>
    );
}
